// ensureLazySystemDatabases.js — backfill catch-all collections when nodes exist but no collection row does

import { getBaseDatabaseByKey } from 'services/databases/DatabaseReadService';
import { createDatabase } from 'services/databases/DatabaseWriteService';
import { isBaseTypePurgeInFlight } from 'services/databases/systemDatabasePurgeSession';
import { getFirstNodeForDatabase } from 'services/nodes/NodeReadService';
import { getPluralLabel } from 'utilities/Languages';
import { LAZY_SYSTEM_DATABASE_TYPE_KEYS } from 'utilities/lazySystemDatabaseTypes';

const buildDefaultDatabaseKey = (baseKey, typeKey) => (
  `${baseKey}_${typeKey}_all`.replace(/\//g, '_')
);

function normalizeTypeKey(typeKey) {
  return String(typeKey || '').trim().toUpperCase();
}

function baseHasCatchAllDatabase({ databases, typeKey }) {
  const normalizedTypeKey = normalizeTypeKey(typeKey);
  if (!normalizedTypeKey) return false;

  return (Array.isArray(databases) ? databases : []).some((database) => {
    if (normalizeTypeKey(database?.type) !== normalizedTypeKey) return false;
    const filters = database?.filters;
    return !Array.isArray(filters) || filters.length === 0;
  });
}

/**
 * Ensures catch-all collection docs exist for lazy system types that already have nodes.
 * Uses a limit-1 node probe per missing type (no full collection scan).
 */
export async function ensureLazySystemDatabases({
  baseKey,
  userKey,
  databases = [],
  getType,
  language = 'en',
  /** Extra system type keys to ensure even with zero nodes (e.g. connected Calendar → EVENT). */
  forceTypeKeys = [],
} = {}) {
  const cleanBaseKey = String(baseKey || '').trim();
  const cleanUserKey = String(userKey || '').trim();
  if (!cleanBaseKey || !cleanUserKey) return;
  if (isBaseTypePurgeInFlight(cleanBaseKey)) return;

  const forced = new Set(
    (Array.isArray(forceTypeKeys) ? forceTypeKeys : [])
      .map((typeKey) => normalizeTypeKey(typeKey))
      .filter((typeKey) => LAZY_SYSTEM_DATABASE_TYPE_KEYS.has(typeKey)),
  );

  const checks = Array.from(new Set([
    ...LAZY_SYSTEM_DATABASE_TYPE_KEYS,
    ...forced,
  ]))
    .filter((typeKey) => !baseHasCatchAllDatabase({ databases, typeKey }))
    .map(async (typeKey) => {
      if (!forced.has(typeKey)) {
        const existingNode = await getFirstNodeForDatabase({
          type: typeKey,
          baseKey: cleanBaseKey,
        });
        if (!existingNode) return;
      }

      const databaseKey = buildDefaultDatabaseKey(cleanBaseKey, typeKey);
      const existingDatabase = await getBaseDatabaseByKey(cleanBaseKey, databaseKey);
      if (existingDatabase) return;

      const typeDef = typeof getType === 'function' ? getType(typeKey) : null;
      const title = getPluralLabel(typeDef, typeKey, language) || 'Items';

      await createDatabase({
        key: databaseKey,
        baseKey: cleanBaseKey,
        userKey: cleanUserKey,
        database: {
          key: databaseKey,
          type: typeKey,
          title,
          filters: [],
        },
      });
    });

  await Promise.all(checks);
}
